<template>
  <div class="flex h-screen bg-gray-50">
    <SideBar class="flex-shrink-0" />
    <div class="flex-1 overflow-auto relative p-6">
      <h1 class="text-2xl font-bold mb-4">Sales Activity Gaps</h1>

      <!-- Filters Section -->
      <div class="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100">
        <div class="flex flex-col gap-4">
          <!-- User Filter -->
          <div class="w-full">
            <label class="block text-sm font-medium text-gray-700 mb-1">Select User</label>
            <div class="flex flex-wrap gap-2" v-if="Object.keys(usersFiltered).length">
              <button
                v-for="(name, id) in usersFiltered"
                :key="id"
                @click="setSelectedUser(id)"
                :class="[
                  'px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105',
                  selectedUser == id
                    ? 'bg-green-400 text-black shadow-md'
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                ]"
              >
                {{ name }}
              </button>
            </div>
            <p v-else class="text-gray-500 text-sm">Loading users...</p>
          </div>

          <!-- Date Picker -->
          <div class="w-full md:w-64">
            <label class="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
            <input
              type="date"
              v-model="selectedDate"
              @change="fetchGaps"
              class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-green-400 focus:border-transparent"
            />
          </div>

          <!-- Gap Minutes -->
          <div class="w-full md:w-48">
            <label class="block text-sm font-medium text-gray-700 mb-1">Gap Threshold (min)</label>
            <input
              type="number"
              v-model.number="gapMinutes"
              @change="fetchGaps"
              min="1"
              max="60"
              class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-green-400 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-gray-500">Loading...</div>
      <div v-else-if="error" class="text-red-500">Error: {{ error }}</div>

      <div v-else>
        <!-- Gap Duration Frequency Chart -->
        <div class="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100" v-if="gaps && gaps.length">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Gap Duration Distribution</h2>
          <div class="h-64">
            <canvas ref="gapChart"></canvas>
          </div>
        </div>

        <div class="bg-white rounded shadow p-4">
          <div v-if="debugInfo" class="mb-4 text-sm text-gray-500 border-b pb-2">
            <p>Showing data for: <span class="font-medium text-gray-900">{{ formatDate(debugInfo.date) }}</span></p>
            <p>Work Hours: <span class="font-medium text-gray-900">{{ formatTime(debugInfo.workStart) }} - {{ formatTime(debugInfo.workEnd) }}</span></p>
            <p class="text-xs text-gray-400 mt-1">{{ debugInfo.info }}</p>
          </div>

          <!-- Header with total -->
          <div class="flex justify-between items-center mb-4" v-if="gaps && gaps.length">
            <span class="text-sm text-gray-500">{{ gaps.length }} gap(s) found</span>
            <span class="text-lg font-bold" :class="totalGapMinutes > 60 ? 'text-red-500' : 'text-gray-900'">
              Total: {{ totalGapMinutes }} min
            </span>
          </div>

          <table class="min-w-full divide-y divide-gray-200" v-if="gaps && gaps.length">
            <thead>
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration (min)</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(gap, index) in gaps" :key="index">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatTime(gap.startTime) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatTime(gap.endTime) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-bold" :class="gap.durationMinutes > 15 ? 'text-red-500' : 'text-gray-900'">
                  {{ Math.round(gap.durationMinutes, 2) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-center py-8 text-gray-500">
              No gaps found! Great work.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SideBar from '@/components/SideBar.vue'
import { getGaps } from '@/api/calls'
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import Chart from 'chart.js/auto'

export default {
  name: 'GapsView',
  components: { SideBar },
  data() {
    return {
      loading: false,
      error: null,
      gaps: [],
      debugInfo: null,
      selectedUser: null,
      selectedDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'), // Default to yesterday
      gapMinutes: 5, // Default gap threshold in minutes
      chartInstance: null
    }
  },
  computed: {
    ...mapGetters('leads', ['usersFiltered']),
    totalGapMinutes() {
      return this.gaps.reduce((sum, gap) => sum + (gap.durationMinutes || 0), 0)
    },
    gapFrequency() {
      // Count occurrences of each rounded duration
      const frequency = {}
      this.gaps.forEach(gap => {
        const minutes = Math.round(gap.durationMinutes)
        frequency[minutes] = (frequency[minutes] || 0) + 1
      })
      // Sort by duration and return as arrays
      const sortedDurations = Object.keys(frequency).map(Number).sort((a, b) => a - b)
      return {
        labels: sortedDurations.map(d => `${d} min`),
        data: sortedDurations.map(d => frequency[d])
      }
    }
  },
  watch: {
    gaps: {
      handler() {
        this.$nextTick(() => this.renderChart())
      },
      deep: true
    }
  },
  async created() {
    await this.$store.dispatch('leads/fetchUserMap')
    // Set default user to the first one
    const userIds = Object.keys(this.usersFiltered)
    if (userIds.length) {
      this.selectedUser = userIds[0]
    }
    this.fetchGaps()
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },
  methods: {
    setSelectedUser(userId) {
      this.selectedUser = userId
      this.fetchGaps()
    },
    async fetchGaps() {
      if (!this.selectedUser) return
      this.loading = true
      this.error = null
      try {
        const { data } = await getGaps(this.selectedUser, this.selectedDate, this.gapMinutes)
        if (data.success) {
          this.gaps = data.gaps
          this.debugInfo = data.debug
        } else {
          this.error = "API returned failure"
        }
      } catch (err) {
        this.error = err.message || "Failed to fetch gaps"
      } finally {
        this.loading = false
      }
    },
    renderChart() {
      const canvas = this.$refs.gapChart
      if (!canvas || !this.gaps.length) return

      // Destroy existing chart
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }

      const { labels, data } = this.gapFrequency

      this.chartInstance = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Number of Occurrences',
            data,
            backgroundColor: 'rgba(74, 222, 128, 0.6)',
            borderColor: 'rgb(34, 197, 94)',
            borderWidth: 2,
            borderRadius: 6,
            hoverBackgroundColor: 'rgba(34, 197, 94, 0.8)'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 800,
            easing: 'easeOutQuart'
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                precision: 0
              },
              title: {
                display: true,
                text: 'Occurrences',
                font: { weight: 'bold' }
              },
              grid: {
                color: 'rgba(200, 200, 200, 0.2)'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Gap Duration',
                font: { weight: 'bold' }
              },
              grid: {
                display: false
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              cornerRadius: 8,
              titleFont: { size: 14, weight: 'bold' },
              callbacks: {
                label: (context) => `${context.parsed.y} time(s)`
              }
            }
          }
        }
      })
    },
    formatTime(isoString) {
      if (!isoString) return '-'
      return dayjs(isoString).format('h:mm A')
    },
    formatDate(isoString) {
      if (!isoString) return '-'
      return dayjs(isoString).format('MMMM D, YYYY')
    }
  }
}
</script>

